import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { QueryConfig } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { queryOptions, useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api-client';
import { ItemTemplateEntityWithSchema } from '@/types/api';

export const getItemTemplateSchema = async (
  orgId: string,
  itemTemplateId: string,
  version?: number,
): Promise<ItemTemplateEntityWithSchema> => {
  const params = version != null ? `?v=${version}` : '';
  const { data } = await api.get<undefined, ApiResponse<ItemTemplateEntityWithSchema>>(
    `/orgs/${orgId}/item-templates/${itemTemplateId}/schema${params}`,
  );
  return data;
};

export const getItemTemplateSchemaQueryKey = (
  orgId?: string | null,
  itemTemplateId?: string | null,
  version?: number | null,
) => {
  const queryKey = ['item-template-schema'];
  if (orgId) queryKey.push(orgId);
  if (itemTemplateId) queryKey.push(itemTemplateId);
  if (version != null) queryKey.push(String(version));
  return queryKey;
};

export const getItemTemplateSchemaQueryOptions = (
  orgId: string | undefined | null,
  itemTemplateId: string | undefined | null,
  version?: number | null,
) => {
  return queryOptions({
    queryKey: getItemTemplateSchemaQueryKey(orgId, itemTemplateId, version),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => getItemTemplateSchema(orgId!, itemTemplateId!, version ?? undefined),
    enabled: !!orgId && !!itemTemplateId,
  });
};

interface UseItemTemplateSchemaOptions {
  version?: number;
  queryConfig?: QueryConfig<typeof getItemTemplateSchemaQueryOptions>;
}

export const useItemTemplateSchema = (
  itemTemplateId: string | undefined,
  { version, queryConfig }: UseItemTemplateSchemaOptions = {},
) => {
  const { orgId } = useScopeIds('org');
  return useQuery({
    ...getItemTemplateSchemaQueryOptions(orgId, itemTemplateId, version),
    ...queryConfig,
  });
};
